// Site content lives here so pages stay focused on layout.

export const CONTACT = {
  email: 'support@autisticdev.com',
  location: 'BTM, Bannerghatta Road, Bangalore',
};

export const SOCIAL = {
  linkedin: 'https://www.linkedin.com/company/106766319',
  github: 'https://github.com/autistic-dev',
};

export const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/contact', label: 'Contact' },
];

export const STRENGTHS = [
  'Pattern recognition',
  'Deep focus',
  'Radical honesty',
  'Systems thinking',
  'Attention to detail',
  'Lateral problem-solving',
  'Pixel-level precision',
  'Relentless curiosity',
];

export const STATS = [
  { value: 20, suffix: '%', prefix: '15–', label: 'of people are neurodivergent. We design for all of them.' },
  { value: 100, suffix: '%', label: 'of our builds target WCAG 2.1 AA from day one.' },
  { value: 4, suffix: '.5:1', label: 'minimum colour contrast. No grey-on-grey, ever.' },
  { value: 0, suffix: '', label: 'surprise meetings. Everything has a written agenda.' },
];

export const SERVICES = [
  {
    id: 'web',
    title: 'Web Development',
    description: 'Custom websites tailored to your specific needs with accessibility as a core focus.',
    details: [
      'Responsive design that works on all devices',
      'WCAG 2.1 AA compliance and accessibility features',
      'Performance optimization for fast loading times',
      'Content management systems integration',
      'E-commerce functionality',
    ],
    icon: 'web',
  },
  {
    id: 'app',
    title: 'App Development',
    description: 'Mobile and desktop applications designed with user experience and inclusivity in mind.',
    details: [
      'Native and cross-platform mobile applications',
      'Desktop software for Windows, macOS, and Linux',
      'User-centered design process',
      'Accessible interface design',
      'Integration with existing systems',
    ],
    icon: 'app',
  },
  {
    id: 'consulting',
    title: 'Tech Consulting',
    description: 'Expert advice from a neurodiverse perspective, helping you innovate and solve complex problems.',
    details: [
      'Technical strategy and roadmap planning',
      'Accessibility audits and recommendations',
      'Technology stack selection advice',
      'Process optimization and automation',
      'Team training and skill development',
    ],
    icon: 'consulting',
  },
  {
    id: 'a11y',
    title: 'Accessibility Solutions',
    description: 'Specialized services to ensure your digital products are accessible to all users.',
    details: [
      'WCAG compliance audits and remediation',
      'Assistive technology compatibility testing',
      'User testing with diverse participants',
      'Documentation and training for ongoing compliance',
      'Legal compliance consultation',
    ],
    icon: 'a11y',
  },
];

export const PROCESS = [
  {
    title: 'Listen',
    text: 'It starts with a written brief, not a surprise call. You get a clear list of questions and plenty of time to answer them.',
  },
  {
    title: 'Map',
    text: 'We document everything: specs, edge cases and decisions, before a single line of code. No ambiguity, no guesswork.',
  },
  {
    title: 'Build',
    text: 'A predictable weekly rhythm of demos and written updates. You always know exactly where things stand.',
  },
  {
    title: 'Refine',
    text: 'Accessibility testing with real assistive tech and real people, then we polish until every detail is right.',
  },
];

export const VALUES = [
  { title: 'Inclusion', text: 'We champion diversity and create environments where everyone can thrive.' },
  { title: 'Innovation', text: 'We leverage diverse thinking to find creative solutions to complex problems.' },
  { title: 'Quality', text: 'We pride ourselves on meticulous attention to detail in everything we create.' },
  { title: 'Accessibility', text: 'We believe technology should be usable and accessible to everyone.' },
];

export const TEAM = [
  {
    id: 1,
    name: 'Alex Morgan',
    role: 'Founder & Lead Developer',
    bio: 'Alex brings 10+ years of experience in web development with a passion for creating accessible digital experiences.',
    superpower: 'Spots the bug before it is written',
  },
  {
    id: 2,
    name: 'Jamie Chen',
    role: 'UX/UI Designer',
    bio: 'Jamie specializes in designing intuitive interfaces with a focus on accessibility and inclusive design principles.',
    superpower: 'Can feel a 1px misalignment',
  },
  {
    id: 3,
    name: 'Sam Taylor',
    role: 'Full-Stack Developer',
    bio: 'Sam is an expert in building robust applications with attention to performance and detail.',
    superpower: 'Hyperfocus on demand',
  },
];

export const PROJECT_CATEGORIES = ['All', 'Web', 'Mobile', 'Consulting'];

export const PROJECTS = [
  {
    id: 1,
    title: 'Accessible E-commerce Platform',
    category: 'Web',
    description: 'A fully accessible online shopping experience with innovative navigation for users with diverse needs.',
    challenge: 'Checkout flows that screen-reader and keyboard users routinely abandoned.',
    approach: 'Rebuilt navigation around landmarks and predictable focus order, with a single-page, low-distraction checkout.',
    technologies: ['React', 'Node.js', 'MongoDB'],
    year: '2023',
    hue: 42,
  },
  {
    id: 2,
    title: 'Sensory-Friendly Mobile App',
    category: 'Mobile',
    description: 'An application designed with customizable interfaces to accommodate various sensory preferences.',
    challenge: 'One-size-fits-all UI that overwhelmed users sensitive to motion, colour and sound.',
    approach: 'User-controlled themes, motion and sound profiles that sync across devices.',
    technologies: ['React Native', 'Firebase'],
    year: '2023',
    hue: 280,
  },
  {
    id: 3,
    title: 'Inclusive Learning Platform',
    category: 'Web',
    description: 'Educational software that adapts to different learning styles and cognitive approaches.',
    challenge: 'Courses that assumed a single way of learning.',
    approach: 'Lessons available as text, video and interactive walkthroughs, with learner-controlled pacing.',
    technologies: ['Vue.js', 'Express', 'PostgreSQL'],
    year: '2022',
    hue: 160,
  },
  {
    id: 4,
    title: 'Healthcare Portal Redesign',
    category: 'Consulting',
    description: "Accessibility audit and redesign of a major healthcare provider's patient portal.",
    challenge: 'Patients unable to book appointments or read results with assistive technology.',
    approach: 'A full WCAG audit, a prioritised remediation roadmap and hands-on training for the in-house team.',
    technologies: ['UX Research', 'Accessibility Guidelines'],
    year: '2022',
    hue: 200,
  },
  {
    id: 5,
    title: 'Voice-Activated Task Manager',
    category: 'Mobile',
    description: 'A task management app with advanced voice control for hands-free operation.',
    challenge: 'Task apps that demand fine motor control and constant screen attention.',
    approach: 'A voice-first command model with forgiving, natural phrasing and clear spoken feedback.',
    technologies: ['Flutter', 'Google Speech API'],
    year: '2021',
    hue: 12,
  },
  {
    id: 6,
    title: 'Interactive Data Visualization',
    category: 'Web',
    description: 'Accessible data visualization dashboard for a financial services company.',
    challenge: 'Charts that were meaningless to screen-reader users and colour-blind analysts.',
    approach: 'Colour-safe palettes, sonified trends and data tables behind every chart.',
    technologies: ['D3.js', 'React', 'Accessibility Standards'],
    year: '2021',
    hue: 320,
  },
];
